// src/pages/admin/AddFaq.jsx
import React, { useEffect, useState } from "react";
import {
  Container,
  Paper,
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Switch,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  getAllFaqCategoriesAdmin,
  // make sure you have this in src/services/api.js:
  // export const createFaq = (data) => api.post('/rockstar/admin/faq', data);
  createFaq,
} from "../../services/api";

/**
 * AddFaq.jsx
 * - Select a category (from admin categories)
 * - Enter question & answer
 * - Optionally set published, order
 * - Submits to POST /rockstar/admin/faq { question, answer, categoryId, isPublished, order, metadata }
 *
 * NOTE: Ensure you have createFaq exported from src/services/api.js:
 * export const createFaq = (data) => api.post('/rockstar/admin/faq', data);
 */

export default function AddFaq() {
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [order, setOrder] = useState("");
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: "info", message: "" });

  const loadCategories = async () => {
    setLoading(true);
    try {
      const res = await getAllFaqCategoriesAdmin({ limit: 500 });
      const data = res?.data?.data || [];
      setCategories(data);
      // preselect first active category if none selected
      if (!categoryId && data.length > 0) setCategoryId(data[0].id);
    } catch (err) {
      console.error("Failed to fetch categories", err);
      setSnack({ open: true, severity: "error", message: "Failed to load categories" });
    } finally {
      setLoading(false);
    }
  };

 useEffect(() => {
  let mounted = true;

  async function fetchCategories() {
    setLoading(true);
    try {
      const res = await getAllFaqCategoriesAdmin({ limit: 500 });
      const data = res?.data?.data || [];
      if (!mounted) return;
      setCategories(data);
      if (!categoryId && data.length > 0) setCategoryId(data[0].id);
    } catch (err) {
      console.error("Failed to fetch categories", err);
      if (!mounted) return;
      setSnack({ open: true, severity: "error", message: "Failed to load categories" });
    } finally {
      if (mounted) setLoading(false);
    }
  }

  fetchCategories();

  return () => {
    mounted = false;
  };
}, []); // no eslint-disable needed


  const resetForm = () => {
    setQuestion("");
    setAnswer("");
    setOrder("");
    setIsPublished(true);
  };

  const handleSave = async () => {
    if (!categoryId) {
      setSnack({ open: true, severity: "error", message: "Please select a category" });
      return;
    }
    if (!question.trim()) {
      setSnack({ open: true, severity: "error", message: "Question cannot be empty" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        question: question.trim(),
        answer: answer.trim() || null,
        categoryId: Number(categoryId),
        isPublished: Boolean(isPublished),
        order: order ? Number(order) : null,
        // metadata: optional JSON object, keep null for now
      };

      await createFaq(payload);
      setSnack({ open: true, severity: "success", message: "FAQ added successfully" });
      resetForm();
    } catch (err) {
      console.error("createFaq err", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to add FAQ";
      setSnack({ open: true, severity: "error", message: msg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: 4, borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          Add FAQ
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Add a new FAQ under an existing category.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="category-select-label">Category</InputLabel>
              <Select
                labelId="category-select-label"
                value={categoryId}
                label="Category"
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={loading || categories.length === 0}
              >
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                />
              }
              label="Published"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              fullWidth
              multiline
              rows={2}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Answer (optional)"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              fullWidth
              multiline
              rows={6}
              disabled={loading}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Order (optional)"
              value={order}
              onChange={(e) => setOrder(e.target.value.replace(/[^\d]/g, ""))}
              fullWidth
              disabled={loading}
              type="text"
            />
          </Grid>

          <Grid item xs={12} sx={{ textAlign: "right" }}>
            <Box display="inline-flex" gap={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  resetForm();
                }}
                disabled={loading}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                Save FAQ
              </Button>
            </Box>
          </Grid>
        </Grid>

        {loading && <LinearProgress sx={{ mt: 2 }} />}

        <Snackbar
          open={snack.open}
          autoHideDuration={3500}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Paper>
    </Container>
  );
}
